import { create } from 'zustand';
import { socialSharesApi } from '@/lib/api/socialShares';
import { hasAiSharingConsent } from '@/store/aiSharingConsent';

const currentUser = () => require('@/store/auth').useAuthStore.getState().user;
const memoryEnabled = user => user?.preferences?.memory?.isEnabled !== false;
const canUseSuggestions = userId => currentUser()?.id === userId && hasAiSharingConsent(userId);

// Account-scoped UI cache. Server claims remain authoritative after reload.
export const useSocialSharingStore = create((set, get) => ({
  suggestionsByUser: {},
  getSuggestions: userId => {
    const entry = get().suggestionsByUser[userId];
    return canUseSuggestions(userId) && entry?.memoryEnabled === memoryEnabled(currentUser())
      && entry.expiresAt > Date.now() ? entry.data : undefined;
  },
  loadSuggestions: userId => {
    if (!userId || !canUseSuggestions(userId)) return Promise.resolve(undefined);
    const enabled = memoryEnabled(currentUser());
    const cached = get().suggestionsByUser[userId];
    if (cached?.memoryEnabled === enabled) {
      if (cached.promise) return cached.promise;
      if (cached.expiresAt > Date.now()) return Promise.resolve(cached.data);
    }
    const entry = { memoryEnabled: enabled, expiresAt: 0 };
    const finish = data => {
      if (get().suggestionsByUser[userId] !== entry) return undefined;
      const valid = canUseSuggestions(userId) && memoryEnabled(currentUser()) === enabled;
      const result = valid ? data : undefined;
      set(state => ({ suggestionsByUser: { ...state.suggestionsByUser, [userId]: {
        memoryEnabled: enabled, data: result,
        expiresAt: valid ? Date.now() + (result?.suggestions?.length === 3 ? 600000 : 60000) : 0,
      } } }));
      return result;
    };
    // Share one in-flight request between the referral page, focus events, and the modal.
    entry.promise = Promise.resolve().then(() => canUseSuggestions(userId) && memoryEnabled(currentUser()) === enabled
      ? socialSharesApi.suggestions(false) : undefined).then(finish, () => finish(undefined));
    set(state => ({ suggestionsByUser: { ...state.suggestionsByUser, [userId]: entry } }));
    return entry.promise;
  },
  claimsByUser: {},
  hideAfterByUser: {},
  recordClaims: (userId, claims) => set(state => {
    if (!userId) return state;
    const saved = { ...state.claimsByUser[userId] };
    for (const claim of claims) {
      // A list request started before payout must not undo the success update.
      if (saved[claim.platform]?.status !== 'awarded') saved[claim.platform] = claim;
    }
    const awardedTimes = Object.values(saved).filter(claim => claim.status === 'awarded')
      .map(claim => Date.parse(claim.awardedAt)).filter(Number.isFinite);
    return {
      claimsByUser: { ...state.claimsByUser, [userId]: saved },
      hideAfterByUser: { ...state.hideAfterByUser,
        [userId]: awardedTimes.length ? Math.min(...awardedTimes) + 24 * 60 * 60 * 1000 : null },
    };
  }),
}));
